import React, { useState } from "react";
import { SpacingColumn } from "../../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../../components/BuildingBlocks/SpacingRow";
import { FrontQuestionFormCard } from "../../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import { TContainerStateProps } from "../../../state/TContainerStateProps";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { TOnboardingTabStatus } from "../../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import FrontParagraphSmall from "../../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import { FrontFormSectionTitle } from "../../../components/Front/Simple/FrontFormSectionTitle/FrontFormSectionTitle";
import { FrontFormPrimaryButton } from "../../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { FrontFormSecondaryButton } from "../../../components/Front/Simple/FrontFormSecondaryButton/FrontFormSecondaryButton";
import { WeightBold } from "../../../components/WeightBold/WeightBold";
import { TUsersIdCheckComplyAdvantageMatch } from "../../../../../domain/codecs/UsersIdCheck";

const matchSummary = (m: TUsersIdCheckComplyAdvantageMatch): string =>
    [
        m.date_of_birth ? `born ${m.date_of_birth}` : "",
        m.country_codes.length > 0 ? m.country_codes.join(", ") : "",
        m.aml_types.length > 0 ? m.aml_types.join(", ") : "",
    ]
        .filter((part) => part.length > 0)
        .join(" · ");

const FrontPepDeclarationContainer = (props: React.PropsWithChildren<TContainerStateProps>): JSX.Element => {
    const [confirmingIsMe, setConfirmingIsMe] = useState<boolean>(false);

    const pepDeclaration = props.state.forms.client_case_page.children.pep_declaration;

    if (!pepDeclaration) {
        return <></>;
    }

    if (pepDeclaration.is_step_complete) {
        return (
            <FrontQuestionFormCard title="Identity checks — declaration">
                <FrontParagraphSmall>
                    Thank you. We've recorded your response and your case handler will be in touch
                    if anything further is needed.
                </FrontParagraphSmall>
            </FrontQuestionFormCard>
        );
    }

    return (
        <FrontQuestionFormCard title="Identity checks — declaration">
            <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                <FrontParagraphSmall>
                    As part of our anti-money-laundering checks, we screen everyone against
                    politically-exposed-person and sanctions lists. Your name and date of birth are
                    a possible match for the {pepDeclaration.matched_profiles.length === 1 ? "person" : "people"} below.
                    This is almost always just a coincidence of name and date of birth.
                </FrontParagraphSmall>

                <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                    <FrontFormSectionTitle>
                        Possible {pepDeclaration.matched_profiles.length === 1 ? "match" : "matches"}
                    </FrontFormSectionTitle>

                    <SpacingColumn spacing={FrontSpacing.MINIMUM}>
                        {pepDeclaration.matched_profiles.map((m, i) => (
                            <FrontParagraphSmall key={i}>
                                <WeightBold>{m.matching_name || "Unnamed record"}</WeightBold>
                                {matchSummary(m).length > 0 ? ` — ${matchSummary(m)}` : ""}
                            </FrontParagraphSmall>
                        ))}
                    </SpacingColumn>
                </SpacingColumn>

                {confirmingIsMe
                    ? <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                        <FrontParagraphSmall>
                            You're telling us that <WeightBold>one of the records above does relate to
                            you</WeightBold>. That's completely fine — <WeightBold>your case handler will be
                            in touch</WeightBold> to talk it through. Is that right?
                        </FrontParagraphSmall>
                        <SpacingRow spacing={FrontSpacing.SMALL_3} justifyContent="start">
                            <FrontFormPrimaryButton
                                label="Yes, one of these is me"
                                icon="done"
                                onClick={() => props.dispatch({
                                    type: "CLIENT_SUBMIT_PEP_DECLARATION_STEP_FORM_AS_COMPLETE",
                                    payload: true,
                                })}
                            />
                            <FrontFormSecondaryButton
                                label="Go back"
                                onClick={() => setConfirmingIsMe(false)}
                            />
                        </SpacingRow>
                    </SpacingColumn>
                    : <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                        <FrontParagraphSmall>
                            Please confirm whether any of these records relate to you.
                        </FrontParagraphSmall>
                        <SpacingRow spacing={FrontSpacing.SMALL_3} justifyContent="start">
                            <FrontFormPrimaryButton
                                label="None of these are me"
                                icon="done"
                                onClick={() => props.dispatch({
                                    type: "CLIENT_SUBMIT_PEP_DECLARATION_STEP_FORM_AS_COMPLETE",
                                    payload: false,
                                })}
                            />
                            <FrontFormSecondaryButton
                                label="One of these is me"
                                onClick={() => setConfirmingIsMe(true)}
                            />
                        </SpacingRow>
                    </SpacingColumn>}
            </SpacingColumn>
        </FrontQuestionFormCard>
    );
};

export const getPepDeclarationStatus = (props: TContainerStateProps): TOnboardingTabStatus => {
    const pepDeclaration = props.state.forms.client_case_page.children.pep_declaration;

    if (!pepDeclaration || pepDeclaration.is_step_complete === true) {
        return "completed";
    }

    return "active";
};

export default FrontPepDeclarationContainer;
